
import { onMounted, reactive, ref, computed, nextTick, onUnmounted } from "vue";
import XAtable from "@/components/XATable.vue";
import UserSelect from "@/components/UserSelect.vue";
import {
  sysExpendQueryList,
  sysExpendDay,
  sysExpendGmt,
  sysExpendMonth,
  sysUserAdsQueryList,
} from "@/api/api";
import { getUserInfo } from "@/utils/auth";
import type { FormInstance } from "ant-design-vue";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import * as echarts from "echarts";
type RangeValue = [Dayjs, Dayjs] | null;

interface SearchFormState {
  adsId: string | null;
  dateRange: RangeValue;
  userId: string | number | null;
  createBy?: string | number;
  userType: number | null;
  pageNo: number;
  pageSize: number;
}

export default {
  name: "ExpendList",
  components: {
    XAtable,
    UserSelect,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      adsId: null,
      dateRange: null,
      userId: null,
      createBy: undefined,
      userType: null,
      pageNo: 1,
      pageSize: 10,
    });

    const formRef = ref<FormInstance>();
    const searchFormRef = ref<FormInstance>();
    const tableData = ref([]);
    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const dateFormat = ref("YYYY-MM-DD");
    const loading = ref(false);
    const chartLoading = ref(false);
    const activeTabKey = ref("day");

    interface CreatorOption {
      id: string | number;
      name: string;
    }

    const CREATOR_FILTER_PAGE_SIZE = 50;
    const creatorFilterList = ref<CreatorOption[]>([]);
    const creatorFilterLoading = ref(false);
    const creatorFilterPageNo = ref(1);
    const creatorFilterTotal = ref(0);
    const creatorFilterKeyword = ref("");

    const getCreatorFilterList = (reset = false) => {
      if (reset) {
        creatorFilterPageNo.value = 1;
        creatorFilterList.value = [];
      }

      creatorFilterLoading.value = true;
      const isNumeric = /^\d+$/.test(creatorFilterKeyword.value);
      const reqParams: Record<string, string | number> = {
        pageNo: creatorFilterPageNo.value,
        pageSize: CREATOR_FILTER_PAGE_SIZE,
      };
      if (creatorFilterKeyword.value) {
        if (isNumeric) {
          reqParams.id = creatorFilterKeyword.value;
        } else {
          reqParams.name = creatorFilterKeyword.value;
        }
      }

      sysUserAdsQueryList(reqParams)
        .then((res) => {
          creatorFilterLoading.value = false;
          if (res.code === "200" && res.data) {
            creatorFilterTotal.value = res.data.total ?? 0;
            const raw = res.data.records || [];
            const records: CreatorOption[] = raw.map(
              (row: Record<string, unknown>) => ({
                id: (row.id ?? row.userId ?? row.createBy) as string | number,
                name: String(
                  row.name ?? row.userName ?? row.createByName ?? ""
                ),
              })
            );
            if (reset) {
              creatorFilterList.value = records;
            } else {
              creatorFilterList.value = [
                ...creatorFilterList.value,
                ...records,
              ];
            }
          }
        })
        .catch(() => {
          creatorFilterLoading.value = false;
        });
    };

    const handleCreatorFilterSearch = (value: string) => {
      creatorFilterKeyword.value = value || "";
      getCreatorFilterList(true);
    };

    const handleCreatorFilterScroll = (e: any) => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
        if (
          creatorFilterList.value.length < creatorFilterTotal.value &&
          !creatorFilterLoading.value
        ) {
          creatorFilterPageNo.value += 1;
          getCreatorFilterList(false);
        }
      }
    };

    const handleCreatorFilterFocus = () => {
      if (creatorFilterList.value.length === 0) {
        creatorFilterKeyword.value = "";
        getCreatorFilterList(true);
      }
    };

    const handleCreatorFilterClear = () => {
      creatorFilterKeyword.value = "";
      getCreatorFilterList(true);
    };

    // 图表数据
    const dayChartData = ref<any[]>([]);
    const gmtChartData = ref<any[]>([]);
    const monthChartData = ref<any[]>([]);

    // 图表引用
    const dayChartRef = ref<HTMLElement | null>(null);
    const gmtChartRef = ref<HTMLElement | null>(null);
    const monthChartRef = ref<HTMLElement | null>(null);
    let dayChart: echarts.ECharts | null = null;
    let gmtChart: echarts.ECharts | null = null;
    let monthChart: echarts.ECharts | null = null;

    // 日期快捷选项
    const dateRanges = {
      昨日: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
      本周: [dayjs().startOf("week"), dayjs().endOf("week")],
      本月: [dayjs().startOf("month"), dayjs().endOf("month")],
      上月: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    };

    // roleType === 0 时显示内部消耗及用户类型筛选
    const getUserRoleType = () => {
      const userInfo = getUserInfo();
      return userInfo?.roleType;
    };
    const showInternalConsumption = () => getUserRoleType() === 0;

    // 格式化金额
    const formatMoney = (value: number) => {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(2) + "M";
      } else if (value >= 1000) {
        return (value / 1000).toFixed(2) + "K";
      }
      return value.toFixed(2);
    };

    const createByColumn = {
      title: "创建者(户)",
      key: "createBy",
      dataIndex: "createBy",
      width: 120,
      slots: {
        customRender: "createBy",
      },
    };

    // 列配置（创建者(户) 仅管理员 roleType === 0 展示）
    const columns = computed(() => {
      const base: any[] = [
        {
          title: "广告账户ID",
          key: "adsId",
          dataIndex: "adsId",
          width: 150,
        },
        {
          title: "账户名称",
          key: "adsName",
          dataIndex: "adsName",
          width: 200,
        },
      ];
      if (getUserRoleType() === 0) {
        base.push(createByColumn);
      }
      base.push(
        {
          title: "时区",
          key: "gmt",
          dataIndex: "gmt",
          width: 150,
          sorter: {
            compare: (a: any, b: any) => {
              const aVal = a.gmt || "";
              const bVal = b.gmt || "";
              return aVal.localeCompare(bVal);
            },
            multiple: 2,
          },
        },
        {
          title: "消耗金额($)",
          key: "totalExpend",
          dataIndex: "totalExpend",
          width: 150,
          slots: {
            customRender: "totalExpend",
          },
          sorter: {
            compare: (a: any, b: any) => {
              return (a.totalExpend || 0) - (b.totalExpend || 0);
            },
            multiple: 1,
          },
        }
      );
      return base;
    });

    const search = () => {
      searchFormState.pageNo = 1;
      getData();
      getChartData();
    };

    const resetForm = () => {
      searchFormState.pageNo = 1;
      searchFormRef.value?.resetFields();
      if (getUserRoleType() === 0) {
        creatorFilterKeyword.value = "";
        creatorFilterPageNo.value = 1;
        creatorFilterList.value = [];
        creatorFilterTotal.value = 0;
      } else {
        searchFormState.createBy = undefined;
      }
      getData();
      getChartData();
    };

    const getData = () => {
      loading.value = true;
      const params: any = {
        pageNo: searchFormState.pageNo,
        pageSize: searchFormState.pageSize,
      };

      if (searchFormState.adsId) {
        params.adsId = searchFormState.adsId;
      }

      // 处理日期区间
      if (searchFormState.dateRange && Array.isArray(searchFormState.dateRange) && searchFormState.dateRange.length === 2) {
        params.beginTime = searchFormState.dateRange[0];
        params.endTime = searchFormState.dateRange[1];
      }

      if (searchFormState.userId) {
        params.userId = searchFormState.userId;
      }

      if (getUserRoleType() === 0) {
        if (
          searchFormState.createBy !== undefined &&
          searchFormState.createBy !== null &&
          searchFormState.createBy !== ""
        ) {
          params.createBy = searchFormState.createBy;
        }
      }

      if (searchFormState.userType !== null && searchFormState.userType !== undefined) {
        params.userType = searchFormState.userType;
      }

      sysExpendQueryList(params)
        .then((res) => {
          loading.value = false;
          if (res.code === "200") {
            total.value = res.data?.total || 0;
            tableData.value = res.data?.records || [];
          }
        })
        .catch(() => {
          loading.value = false;
        });
    };

    const pageNoChange = (val: number) => {
      searchFormState.pageNo = val;
      pageNo.value = val;
      getData();
    };

    const onShowSizeChange = (current: number, size: number) => {
      pageSize.value = size;
      searchFormState.pageSize = size;
      searchFormState.pageNo = 1;
      pageNo.value = 1;
      getData();
    };

    // 获取行类名，最后一行加粗
    const getRowClassName = (record: any, index: number) => {
      if (index === tableData.value.length - 1) {
        return "last-row-bold";
      }
      return "";
    };

    // 获取图表统计数据
    const getChartData = () => {
      chartLoading.value = true;
      const params: any = {};

      if (searchFormState.adsId) {
        params.adsId = searchFormState.adsId;
      }

      if (searchFormState.dateRange && Array.isArray(searchFormState.dateRange) && searchFormState.dateRange.length === 2) {
        params.beginTime = searchFormState.dateRange[0];
        params.endTime = searchFormState.dateRange[1];
      }

      if (searchFormState.userId) {
        params.userId = searchFormState.userId;
      }

      if (getUserRoleType() === 0) {
        if (
          searchFormState.createBy !== undefined &&
          searchFormState.createBy !== null &&
          searchFormState.createBy !== ""
        ) {
          params.createBy = searchFormState.createBy;
        }
      }

      if (searchFormState.userType !== null && searchFormState.userType !== undefined) {
        params.userType = searchFormState.userType;
      }

      Promise.all([
        sysExpendDay(params),
        sysExpendGmt(params),
        sysExpendMonth(params),
      ])
        .then(([dayRes, gmtRes, monthRes]) => {
          chartLoading.value = false;
          if (dayRes.code === "200") {
            dayChartData.value = dayRes.data || [];
          }
          if (gmtRes.code === "200") {
            gmtChartData.value = gmtRes.data || [];
          }
          if (monthRes.code === "200") {
            monthChartData.value = monthRes.data || [];
          }
          // 更新图表
          nextTick(() => {
            updateDayChart();
            updateGmtChart();
            updateMonthChart();
          });
        })
        .catch(() => {
          chartLoading.value = false;
        });
    };

    // 初始化按天统计图表
    const initDayChart = () => {
      if (!dayChartRef.value) return;
      dayChart = echarts.init(dayChartRef.value);
      updateDayChart();
    };

    // 更新按天统计图表
    const updateDayChart = () => {
      if (!dayChart) return;

      const data = dayChartData.value || [];
      const categories = data.map((item: any) => item.day || "");
      const innerExpend = data.map((item: any) => item.innerExpend || 0);
      const outerExpend = data.map((item: any) => item.outerExpend || 0);
      const totalExpend = data.map((item: any) => item.totalExpend || 0);

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (params: any) => {
            let result = params[0].name + "<br/>";
            params.forEach((item: any) => {
              result += `${item.marker}${item.seriesName}: $${(item.value || 0).toFixed(2)}<br/>`;
            });
            return result;
          },
        },
        legend: {
          data: showInternalConsumption()
            ? ["内部消耗", "外部消耗", "总消耗"]
            : ["总消耗"],
          top: 10,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: categories,
          axisLabel: {
            rotate: categories.length > 7 ? 45 : 0,
            fontSize: 11,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter: (value: number) => {
              if (value >= 10000) {
                return "$" + (value / 10000).toFixed(1) + "万";
              }
              return "$" + value.toFixed(0);
            },
          },
        },
        series: [
          ...(showInternalConsumption()
            ? [
                {
                  name: "内部消耗",
                  type: "bar",
                  barGap: 0,
                  data: innerExpend,
                  itemStyle: { color: "#1890ff" },
                  label: {
                    show: true,
                    position: "top",
                    formatter: (params: any) =>
                      "$" + (params.value || 0).toFixed(2),
                    fontSize: 11,
                  },
                },
                {
                  name: "外部消耗",
                  type: "bar",
                  barGap: 0,
                  data: outerExpend,
                  itemStyle: { color: "#52c41a" },
                  label: {
                    show: true,
                    position: "top",
                    formatter: (params: any) =>
                      "$" + (params.value || 0).toFixed(2),
                    fontSize: 11,
                  },
                },
              ]
            : []),
          {
            name: "总消耗",
            type: "bar",
            barGap: 0,
            data: totalExpend,
            itemStyle: { color: "#faad14" },
            label: {
              show: true,
              position: "top",
              formatter: (params: any) =>
                "$" + (params.value || 0).toFixed(2),
              fontSize: 11,
            },
          },
        ],
      };

      dayChart.setOption(option);
    };

    // 初始化按时区统计图表
    const initGmtChart = () => {
      if (!gmtChartRef.value) return;
      gmtChart = echarts.init(gmtChartRef.value);
      updateGmtChart();
    };

    // 更新按时区统计图表
    const updateGmtChart = () => {
      if (!gmtChart) return;

      const data = gmtChartData.value || [];
      const categories = data.map((item: any) => item.gmt || "");
      const innerExpend = data.map((item: any) => item.innerExpend || 0);
      const outerExpend = data.map((item: any) => item.outerExpend || 0);
      const totalExpend = data.map((item: any) => item.totalExpend || 0);

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (params: any) => {
            let result = params[0].name + "<br/>";
            params.forEach((item: any) => {
              result += `${item.marker}${item.seriesName}: $${(item.value || 0).toFixed(2)}<br/>`;
            });
            return result;
          },
        },
        legend: {
          data: showInternalConsumption()
            ? ["内部消耗", "外部消耗", "总消耗"]
            : ["总消耗"],
          top: 10,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: categories,
          axisLabel: {
            rotate: categories.length > 7 ? 45 : 0,
            fontSize: 11,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter: (value: number) => {
              if (value >= 10000) {
                return "$" + (value / 10000).toFixed(1) + "万";
              }
              return "$" + value.toFixed(0);
            },
          },
        },
        series: [
          ...(showInternalConsumption()
            ? [
                {
                  name: "内部消耗",
                  type: "bar",
                  barGap: 0,
                  data: innerExpend,
                  itemStyle: { color: "#1890ff" },
                  label: {
                    show: true,
                    position: "top",
                    formatter: (params: any) =>
                      "$" + (params.value || 0).toFixed(2),
                    fontSize: 11,
                  },
                },
                {
                  name: "外部消耗",
                  type: "bar",
                  barGap: 0,
                  data: outerExpend,
                  itemStyle: { color: "#52c41a" },
                  label: {
                    show: true,
                    position: "top",
                    formatter: (params: any) =>
                      "$" + (params.value || 0).toFixed(2),
                    fontSize: 11,
                  },
                },
              ]
            : []),
          {
            name: "总消耗",
            type: "bar",
            barGap: 0,
            data: totalExpend,
            itemStyle: { color: "#faad14" },
            label: {
              show: true,
              position: "top",
              formatter: (params: any) =>
                "$" + (params.value || 0).toFixed(2),
              fontSize: 11,
            },
          },
        ],
      };

      gmtChart.setOption(option);
    };

    // 初始化按月统计图表
    const initMonthChart = () => {
      if (!monthChartRef.value) return;
      monthChart = echarts.init(monthChartRef.value);
      updateMonthChart();
    };

    // 更新按月统计图表
    const updateMonthChart = () => {
      if (!monthChart) return;

      const data = monthChartData.value || [];
      const categories = data.map((item: any) => item.month || "");
      const innerExpend = data.map((item: any) => item.innerExpend || 0);
      const outerExpend = data.map((item: any) => item.outerExpend || 0);
      const totalExpend = data.map((item: any) => item.totalExpend || 0);

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (params: any) => {
            let result = params[0].name + "<br/>";
            params.forEach((item: any) => {
              result += `${item.marker}${item.seriesName}: $${(item.value || 0).toFixed(2)}<br/>`;
            });
            return result;
          },
        },
        legend: {
          data: showInternalConsumption()
            ? ["内部消耗", "外部消耗", "总消耗"]
            : ["总消耗"],
          top: 10,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: categories,
          axisLabel: {
            rotate: categories.length > 7 ? 45 : 0,
            fontSize: 11,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter: (value: number) => {
              if (value >= 10000) {
                return "$" + (value / 10000).toFixed(1) + "万";
              }
              return "$" + value.toFixed(0);
            },
          },
        },
        series: [
          ...(showInternalConsumption()
            ? [
                {
                  name: "内部消耗",
                  type: "bar",
                  barGap: 0,
                  data: innerExpend,
                  itemStyle: { color: "#1890ff" },
                  label: {
                    show: true,
                    position: "top",
                    formatter: (params: any) =>
                      "$" + (params.value || 0).toFixed(2),
                    fontSize: 11,
                  },
                },
                {
                  name: "外部消耗",
                  type: "bar",
                  barGap: 0,
                  data: outerExpend,
                  itemStyle: { color: "#52c41a" },
                  label: {
                    show: true,
                    position: "top",
                    formatter: (params: any) =>
                      "$" + (params.value || 0).toFixed(2),
                    fontSize: 11,
                  },
                },
              ]
            : []),
          {
            name: "总消耗",
            type: "bar",
            barGap: 0,
            data: totalExpend,
            itemStyle: { color: "#faad14" },
            label: {
              show: true,
              position: "top",
              formatter: (params: any) =>
                "$" + (params.value || 0).toFixed(2),
              fontSize: 11,
            },
          },
        ],
      };

      monthChart.setOption(option);
    };

    // 窗口大小改变时重新调整图表
    const handleResize = () => {
      dayChart?.resize();
      gmtChart?.resize();
      monthChart?.resize();
    };

    // 处理 Tab 切换
    const handleTabChange = (key: string) => {
      // 使用双重 nextTick 和延迟，确保 Tab 切换动画完成后再操作图表
      nextTick(() => {
        nextTick(() => {
          setTimeout(() => {
            if (key === "day") {
              if (!dayChart && dayChartRef.value) {
                initDayChart();
              } else if (dayChart) {
                // 图表已存在，直接调整大小
                setTimeout(() => {
                  dayChart?.resize();
                }, 50);
              }
            } else if (key === "gmt") {
              if (!gmtChart && gmtChartRef.value) {
                initGmtChart();
              } else if (gmtChart) {
                setTimeout(() => {
                  gmtChart?.resize();
                }, 50);
              }
            } else if (key === "month") {
              if (!monthChart && monthChartRef.value) {
                initMonthChart();
              } else if (monthChart) {
                setTimeout(() => {
                  monthChart?.resize();
                }, 50);
              }
            }
          }, 300);
        });
      });
    };

    onMounted(() => {
      getData();
      getChartData();
      // 初始化第一个图表（按天统计）
      nextTick(() => {
        initDayChart();
      });
      // 监听窗口大小变化
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
      dayChart?.dispose();
      gmtChart?.dispose();
      monthChart?.dispose();
    });

    return {
      searchFormState,
      searchFormRef,
      formRef,
      columns,
      tableData,
      pageNo,
      pageSize,
      dateFormat,
      dateRanges,
      total,
      loading,
      chartLoading,
      activeTabKey,
      handleTabChange,
      dayChartRef,
      gmtChartRef,
      monthChartRef,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      getRowClassName,
      getUserRoleType,
      creatorFilterList,
      creatorFilterLoading,
      handleCreatorFilterSearch,
      handleCreatorFilterScroll,
      handleCreatorFilterFocus,
      handleCreatorFilterClear,
    };
  },
};
